import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import Grid from '@material-ui/core/Grid'
import { Controller, useForm } from 'react-hook-form'
import RoundedModal from '../../../../molecules/RoundedModal'
import SydButton from '../../../../commonDesign/Button'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import Switch from '../../../../molecules/Switch'
import { useAddComponentBlueprintDefinition } from '../../../../../api/denali'
import { decipherError } from '../../../../../utils/decipherError'

const defaultData = JSON.stringify({
  presentations: {
    main: {
      componentType: 'dummy',
      displayName: 'Main',
      config: {},
      styleVariant: 'default',
      styleVariantParams: {}
    }
  }
}, null, 2)

const NewVersionDialog = React.forwardRef(function NewVersionDialog ({ blueprint, onComplete }, ref) {
  const [dialogState, setDialogState] = useState({ open: false })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: addDefinition } = useAddComponentBlueprintDefinition()

  const form = useForm({
    defaultValues: {
      description: '',
      data: defaultData,
      setAsActive: true
    },
    mode: 'onChange'
  })

  const close = useCallback(() => {
    setDialogState({ open: false })
    setError(null)
    form.reset()
  }, [form])

  useImperativeHandle(ref, () => ({
    open: () => setDialogState({ open: true })
  }), [])

  const handleSubmit = useCallback(async (formData) => {
    try {
      setError(null)
      setProcessing(true)

      let parsedData
      try {
        parsedData = JSON.parse(formData.data)
      } catch {
        setError('Invalid JSON in data field')
        return
      }

      await addDefinition({
        blueprintId: blueprint.blueprintId,
        definition: {
          description: formData.description || null,
          data: parsedData
        },
        options: {
          setAsActive: formData.setAsActive
        }
      })

      close()
      onComplete()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [addDefinition, blueprint, close, onComplete])

  return (
    <RoundedModal
      title='Add Definition Version'
      open={dialogState.open}
      onClose={close}
      size='large'
      position='top'
      disablePortal={false}
    >
      <div>
        {dialogState.open ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                control={form.control}
                name='description'
                render={(f) => (
                  <SydLabel label='Version Description'>
                    <SydInput {...f.field} />
                  </SydLabel>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={form.control}
                name='data'
                rules={{ required: { value: true, message: 'Required' } }}
                render={(f) => (
                  <SydLabel required label='Definition Data (JSON)' error={hookFormErrorAdapter(form, f.fieldState)}>
                    <textarea
                      {...f.field}
                      rows={15}
                      style={{
                        width: '100%',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid var(--color-neutral-300)',
                        resize: 'vertical'
                      }}
                    />
                  </SydLabel>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                control={form.control}
                name='setAsActive'
                render={(f) => (
                  <SydLabel label='Set as Active Version'>
                    <Switch
                      checked={f.field.value}
                      onChange={(e) => f.field.onChange(e.target.checked)}
                    />
                  </SydLabel>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <SydModalActions>
                {error ? <div className='__error'>{error}</div> : null}
                <SydButton disabled={processing} variant='ghost' size='md' onClick={close}>
                  Cancel
                </SydButton>
                <SydButton
                  processing={processing}
                  variant='primary'
                  size='md'
                  onClick={form.handleSubmit(handleSubmit)}
                >
                  Add Version
                </SydButton>
              </SydModalActions>
            </Grid>
          </Grid>
        ) : null}
      </div>
    </RoundedModal>
  )
})

NewVersionDialog.propTypes = {
  blueprint: PropTypes.object.isRequired,
  allVersions: PropTypes.array,
  onComplete: PropTypes.func.isRequired
}

export default NewVersionDialog
